import React, { useState, useEffect } from 'react';
import { Medium, ChatMessage, GuestUser } from '../types';
import { ALL_MEDIUMS, SARAH_MEDIUM } from '../data/mediums';
import { useAdmin } from '../contexts/AdminContext';
import { useClient } from '../contexts/ClientContext';
import LiveChat from './LiveChat';
import { 
    MessageCircleIcon, 
    SparklesIcon, 
    StarIcon, 
    ShieldIcon, 
    LockIcon, 
    PhoneIcon, 
    ChevronRightIcon, 
    SendIcon,
    SearchIcon,
    MailIcon,
    MinusIcon,
    UserIcon
} from './Icons';

interface MessagesPageProps {
    onNavigate: (page: string) => void;
    onOpenSarah?: () => void;
    guestUser?: GuestUser | null;
    initialMedium?: Medium | null;
    onClearInitialMedium?: () => void;
}

export const MessagesPage: React.FC<MessagesPageProps> = ({
    onNavigate,
    onOpenSarah,
    guestUser = null,
    initialMedium = null,
    onClearInitialMedium
}) => {
    const { sessions, sessionsHistory } = useAdmin();
    const { client, openAuthModal } = useClient();
    
    const [selectedMedium, setSelectedMedium] = useState<Medium | null>(initialMedium || null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<'inbox' | 'directory'>('inbox');

    const activeClientId = client?.id || (guestUser?.phone ? `guest_${guestUser.phone.replace(/\s+/g, '')}` : 'guest');

    useEffect(() => {
        if (initialMedium) {
            setSelectedMedium(initialMedium);
            if (typeof window !== 'undefined') {
                window.history.replaceState(null, '', `/messages/id=${activeClientId}/chat${initialMedium.id}`);
            }
            if (onClearInitialMedium) {
                onClearInitialMedium();
            }
            return;
        }

        // SÉCURITÉ ABSOLUE DANS MESSAGESPAGE :
        if (typeof window !== 'undefined') {
            const href = window.location.href;
            const urlMatch = href.match(/messages\/id=([a-zA-Z0-9_-]+)/i);
            if (urlMatch) {
                const targetClientId = urlMatch[1];
                const isAdmin = Boolean(localStorage.getItem('admin_logged_user'));
                if (targetClientId !== 'guest' && (!client || client.id !== targetClientId) && !isAdmin) {
                    setSelectedMedium(null);
                    openAuthModal();
                    const safeClientId = client?.id || 'guest';
                    window.history.replaceState(null, '', `/messages/id=${safeClientId}`);
                    return;
                }
            }
            if (href.includes('/sarah') || href.includes('chat9999') || href.includes('chat=9999')) {
                setSelectedMedium(SARAH_MEDIUM);
                return;
            }
            const chatMatch = href.match(/chat(\d+)/i) || href.match(/chat=(\d+)/i) || href.match(/medium=(\d+)/i);
            if (chatMatch) {
                const medId = parseInt(chatMatch[1], 10);
                if (medId === 9999) {
                    setSelectedMedium(SARAH_MEDIUM);
                } else {
                    const found = ALL_MEDIUMS.find(m => m.id === medId);
                    if (found) {
                        setSelectedMedium(found);
                    }
                }
            }
        }
    }, [initialMedium, activeClientId]);

    const handleSelectMedium = (med: Medium) => {
        setSelectedMedium(med);
        if (typeof window !== 'undefined') {
            const chatSuffix = med.id === 9999 ? 'sarah' : `chat${med.id}`;
            window.history.replaceState(null, '', `/messages/id=${activeClientId}/${chatSuffix}`);
        }
    };

    const handleCloseOrMinimizeChat = () => {
        setSelectedMedium(null);
        if (typeof window !== 'undefined') {
            window.history.replaceState(null, '', `/messages/id=${activeClientId}`);
        }
    };

    // ISOLATION TOTALE : Filtrer STRICTEMENT les sessions appartenant au client connecté
    const currentClientId = client?.id || '';
    const currentClientPhone = client?.phone || guestUser?.phone || '';

    // Filtrer les sessions STRICTEMENT pour ce compte client
    const clientIsolatedSessions = [...sessions, ...sessionsHistory].filter(s => {
        if (currentClientId) {
            return s.clientId === currentClientId || (s.id && s.id.includes(currentClientId));
        }
        if (currentClientPhone) {
            const cleanPhone = currentClientPhone.replace(/\s+/g, '');
            return (s.userPhone && s.userPhone.replace(/\s+/g, '') === cleanPhone) || 
                   (s.clientPhone && s.clientPhone.replace(/\s+/g, '') === cleanPhone) ||
                   (s.id && s.id.includes(cleanPhone));
        }
        return false;
    });
    
    // 1. Discussions RÉELLES ET PERMANENTES du client (persistance locale + serveur)
    const ALL_CONVERSATION_TARGETS = [SARAH_MEDIUM, ...ALL_MEDIUMS];

    const activeConversations = ALL_CONVERSATION_TARGETS.map(med => {
        // Source A : Sessions synchronisées en mémoire / serveur
        const medSessions = clientIsolatedSessions.filter(s => 
            s.mediumId === med.id || 
            String(s.mediumId) === String(med.id) ||
            (med.id === 9999 && (s.id.startsWith('sarah_') || s.id.includes('sarah') || String(s.mediumId) === '9999'))
        );
        const lastSession = medSessions.sort((a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime())[0];

        // Source B : Sauvegarde locale permanente dans le navigateur (résistant à la fermeture)
        let localMessages: ChatMessage[] = [];
        let localLastDate: Date | null = null;
        if (typeof window !== 'undefined') {
            const keysToTry = [
                `astro_chat_${activeClientId}_m${med.id}`,
                med.id === 9999 ? `astro_chat_${activeClientId}_sarah` : null,
                currentClientPhone ? `astro_chat_${currentClientPhone.replace(/\s+/g, '')}_m${med.id}` : null
            ].filter(Boolean) as string[];

            for (const k of keysToTry) {
                const saved = localStorage.getItem(k);
                if (saved) {
                    try {
                        const parsed = JSON.parse(saved);
                        if (parsed && Array.isArray(parsed.messages) && parsed.messages.length > localMessages.length) {
                            localMessages = parsed.messages;
                            if (parsed.messages.slice(-1)[0]?.timestamp) {
                                localLastDate = new Date(parsed.messages.slice(-1)[0].timestamp);
                            }
                        }
                    } catch(e) {}
                }
            }
        }

        const combinedMessages = (lastSession?.messages && lastSession.messages.length >= localMessages.length) 
            ? lastSession.messages 
            : localMessages;

        const hasHistory = Array.isArray(combinedMessages) && combinedMessages.length > 0;
        if (!hasHistory) return null;

        const lastMsg = combinedMessages.slice(-1)[0];
        const lastActivityDate = lastSession?.lastActivity || localLastDate || (lastMsg?.timestamp ? new Date(lastMsg.timestamp) : new Date());

        return {
            medium: med,
            lastSession: lastSession || { 
                id: med.id === 9999 ? `sarah_${activeClientId}` : `session_${activeClientId}_m${med.id}`, 
                messages: combinedMessages, 
                lastActivity: lastActivityDate 
            },
            hasHistory: true,
            lastMessageText: lastMsg?.text || `Discussion active`,
            lastMessageTime: lastMsg?.timestamp ? new Date(lastMsg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'Récemment',
            lastActivityTimestamp: new Date(lastActivityDate).getTime(),
            hasUnread: lastSession?.hasUnreadClient || false,
            isOnline: med.status === 'available'
        };
    }).filter(Boolean).sort((a: any, b: any) => b.lastActivityTimestamp - a.lastActivityTimestamp) as {
        medium: Medium;
        lastSession: any;
        hasHistory: boolean;
        lastMessageText: string;
        lastMessageTime: string;
        hasUnread: boolean;
        isOnline: boolean;
    }[];

    const filteredConversations = activeConversations.filter(item => {
        if (!searchQuery.trim()) return true;
        return item.medium.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
               item.medium.specialties.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()));
    });

    // Tri strict par ordre de disponibilité : Disponible (1) > Occupé (2) > Absent (3)
    const directoryMediums = [...ALL_MEDIUMS].sort((a, b) => {
        const score = (status: string) => status === 'available' ? 0 : status === 'busy' ? 1 : 2;
        return score(a.status) - score(b.status);
    }).filter(med => {
        if (!searchQuery.trim()) return true;
        return med.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
               med.specialties.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()));
    });



    const activeClient = client || guestUser || { name: 'Consultant(e)', phone: '0600000000', id: 'guest' };

    return (
        <div className={`min-h-screen bg-[#08081A] text-white max-w-6xl mx-auto animate-fade-in ${selectedMedium ? "p-0 pb-16" : "pt-4 pb-24 px-2 sm:px-4"}`}>

            
            {/* Header de la Messagerie (Masqué sur mobile quand un chat est ouvert) */}
            <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-3 border-b border-purple-900/40 ${selectedMedium ? 'hidden lg:flex' : 'flex'}`}>
                <div>
                    <div className="flex items-center gap-2">
                        <span className="p-2 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-300">
                            <MessageCircleIcon className="w-6 h-6 text-purple-400" />
                        </span>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-serif font-black tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-100 to-amber-200">
                                Messagerie Privée
                            </h1>
                            <p className="text-xs text-purple-300/80">
                                Espace réservé à <strong className="text-amber-300">{activeClient.name}</strong> • Compte <span className="font-mono text-purple-200">#{client?.id ? client.id.substring(0, 8) : 'Invité'}</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Bouton Assistance Secrétariat Sarah */}
                <button
                    onClick={() => handleSelectMedium(SARAH_MEDIUM)}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-900/80 to-amber-950/80 hover:from-purple-800 hover:to-amber-900 text-amber-300 px-4 py-2 rounded-xl border border-amber-500/40 text-xs font-bold transition-all shadow-md active:scale-95 cursor-pointer shrink-0"
                >
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                    <span>Secrétariat & Recharges (Sarah)</span>
                </button>
            </div>

            {/* Layout en 2 Colonnes Desktop ou Plein Écran Mobile */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
                
                {/* Colonne Gauche : Liste des Conversations / Répertoire */}
                <div className={`lg:col-span-5 space-y-3 ${selectedMedium ? 'hidden lg:block' : 'block'}`}>
                    
                    {/* Switch Onglets : Mes Discussions vs Démarrer une Discussion */}
                    <div className="flex bg-[#12102A] p-1 rounded-2xl border border-purple-900/50 gap-1">
                        <button
                            onClick={() => setActiveTab('inbox')}
                            className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                                activeTab === 'inbox'
                                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
                                    : 'text-purple-300 hover:text-white'
                            }`}
                        >
                            <span>Mes Discussions</span>
                            <span className="text-[10px] bg-black/40 px-1.5 py-0.2 rounded-full font-mono">
                                {activeConversations.length}
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('directory')}
                            className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                                activeTab === 'directory'
                                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
                                    : 'text-purple-300 hover:text-white'
                            }`}
                        >
                            <span>Nouveau Chat</span>
                        </button>
                    </div>

                    {/* Barre de Recherche */}
                    <div className="relative mb-2">
                        <input 
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={activeTab === 'inbox' ? "Rechercher dans mes discussions..." : "Rechercher un voyant..."}
                            className="w-full bg-[#12102A] border border-purple-900/50 focus:border-gold-500/60 rounded-2xl py-2.5 pl-10 pr-4 text-xs text-white placeholder-purple-300/50 focus:outline-none transition-all shadow-inner"
                        />
                        <span className="absolute left-3.5 top-3 text-purple-400">
                            <SearchIcon className="w-4 h-4 text-purple-400" />
                        </span>
                    </div>

                    {/* VUE 1 : MES DISCUSSIONS ACTIVES */}
                    {activeTab === 'inbox' && (
                        <div className="space-y-2.5 max-h-[75vh] overflow-y-auto custom-scrollbar pr-1">
                            {filteredConversations.length === 0 ? (
                                <div className="p-8 rounded-3xl bg-[#12102A]/60 border border-purple-900/30 text-center space-y-4 my-2">
                                    <div className="w-14 h-14 rounded-2xl bg-purple-950/60 border border-purple-500/30 flex items-center justify-center mx-auto shadow-inner">
                                        <MailIcon className="w-7 h-7 text-purple-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-white mb-1">
                                            Aucune conversation en cours
                                        </h3>
                                        <p className="text-xs text-purple-300/70 leading-relaxed max-w-xs mx-auto">
                                            Vous n'avez pas encore d'échange avec nos experts. Choisissez un voyant pour démarrer votre première consultation confidentielle.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setActiveTab('directory')}
                                        className="px-5 py-2.5 rounded-full bg-gradient-to-r from-gold-500 to-amber-500 hover:from-gold-400 hover:to-amber-400 text-black font-black text-xs uppercase tracking-wider shadow-lg transition-all active:scale-95 cursor-pointer"
                                    >
                                        Consulter un voyant disponible
                                    </button>
                                </div>
                            ) : (
                                filteredConversations.map(item => {
                                    const isSelected = selectedMedium?.id === item.medium.id;

                                    return (
                                        <div
                                            key={item.medium.id}
                                            onClick={() => handleSelectMedium(item.medium)}
                                            className={`p-3.5 rounded-2xl border transition-all duration-200 cursor-pointer flex items-center justify-between gap-3 ${
                                                isSelected 
                                                    ? 'bg-gradient-to-r from-purple-900/80 to-indigo-900/80 border-gold-400/80 shadow-[0_0_15px_rgba(251,191,36,0.3)] scale-[1.01]' 
                                                    : 'bg-[#12102A]/90 hover:bg-[#1A1033] border-purple-800/60 shadow-md'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="relative shrink-0">
                                                    <img 
                                                        src={item.medium.imageUrl} 
                                                        alt={item.medium.name}
                                                        className="w-12 h-12 rounded-full object-cover border border-purple-500/40"
                                                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300'; }}
                                                    />
                                                    {item.isOnline && (
                                                        <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-[#12102A] animate-pulse"></span>
                                                    )}
                                                </div>

                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <h3 className="text-sm font-bold text-white truncate">
                                                            {item.medium.name}
                                                        </h3>
                                                        <span className="text-[10px] text-purple-300/60 shrink-0">
                                                            {item.lastMessageTime}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-purple-200/80 truncate font-light mt-0.5">
                                                        {item.lastMessageText}
                                                    </p>
                                                </div>
                                            </div>

                                            {item.hasUnread && (
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <span className="w-2.5 h-2.5 rounded-full bg-pink-500 shadow-[0_0_8px_rgba(236,72,153,0.8)] shrink-0 animate-ping"></span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}

                    {/* VUE 2 : RÉPERTOIRE POUR DÉMARRER UN NOUVEAU CHAT (TRIÉ PAR DISPONIBILITÉ) */}
                    {activeTab === 'directory' && (
                        <div className="space-y-2.5 max-h-[75vh] overflow-y-auto custom-scrollbar pr-1">
                            {directoryMediums.map(med => {
                                const isAvailable = med.status === 'available';
                                const isBusy = med.status === 'busy';
                                const isAway = med.status === 'away';

                                return (
                                    <div
                                        key={med.id}
                                        onClick={() => handleSelectMedium(med)}
                                        className={`p-3.5 rounded-2xl border transition-all cursor-pointer flex items-center justify-between gap-3 shadow-md ${
                                            isAvailable 
                                                ? 'bg-[#120B2E]/90 hover:bg-[#1C1145] border-emerald-500/40 hover:border-emerald-400/80 shadow-[0_0_15px_rgba(16,185,129,0.1)]' 
                                                : isBusy
                                                ? 'bg-[#1A1124]/80 hover:bg-[#231530] border-amber-500/40 hover:border-amber-400/70'
                                                : 'bg-[#0E0B1A]/70 hover:bg-[#151026] border-gray-800/80 opacity-75'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="relative shrink-0">
                                                <img 
                                                    src={med.imageUrl} 
                                                    alt={med.name}
                                                    className={`w-12 h-12 rounded-full object-cover border-2 ${
                                                        isAvailable 
                                                            ? 'border-emerald-400' 
                                                            : isBusy 
                                                            ? 'border-amber-400' 
                                                            : 'border-gray-600'
                                                    }`}
                                                    onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300'; }}
                                                />
                                                <span className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-[#12102A] ${
                                                    isAvailable 
                                                        ? 'bg-emerald-500 animate-pulse' 
                                                        : isBusy 
                                                        ? 'bg-amber-500' 
                                                        : 'bg-zinc-500'
                                                }`}></span>
                                            </div>

                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="text-sm font-bold text-white truncate">
                                                        {med.name}
                                                    </h3>
                                                    <span className="text-[10px] text-gold-400 font-bold">
                                                        {med.rating}
                                                    </span>
                                                    <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded-md ${
                                                        isAvailable 
                                                            ? 'bg-emerald-950 text-emerald-300 border border-emerald-500/50' 
                                                            : isBusy 
                                                            ? 'bg-amber-950 text-amber-300 border border-amber-500/50' 
                                                            : 'bg-zinc-900 text-zinc-400 border border-zinc-700'
                                                    }`}>
                                                        {isAvailable ? 'En ligne' : isBusy ? 'En consultation' : 'Absent'}
                                                    </span>
                                                </div>
                                                <p className="text-[11px] text-purple-300/70 truncate mt-0.5">
                                                    {med.specialties.slice(0, 2).join(' • ')}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="shrink-0">
                                            {isAvailable ? (
                                                <span className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold shadow-md flex items-center gap-1 transition-all">
                                                    <span>Consulter</span>
                                                    <span>→</span>
                                                </span>
                                            ) : isBusy ? (
                                                <span className="px-3 py-1.5 rounded-xl bg-amber-950/80 border border-amber-500/50 text-amber-300 text-[11px] font-bold flex items-center gap-1">
                                                    <span>Occupé</span>
                                                </span>
                                            ) : (
                                                <span className="px-2.5 py-1.5 rounded-xl bg-zinc-900/80 border border-zinc-700 text-zinc-400 text-[11px] font-medium">
                                                    <span>Message</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                </div>

                {/* Colonne Droite : Salon de Chat Plein Écran Dédié */}
                <div className={`lg:col-span-7 ${
                    selectedMedium 
                        ? 'fixed inset-0 bottom-16 z-50 bg-[#08081A] lg:static lg:bottom-auto lg:z-auto lg:bg-transparent flex flex-col h-[calc(100dvh-4rem)] lg:h-auto' 
                        : 'hidden lg:block'
                }`}>
                    {selectedMedium ? (
                        <div className="bg-[#12102A] border border-purple-900/50 rounded-none lg:rounded-3xl overflow-hidden shadow-2xl flex flex-col h-full lg:h-[78vh]">
                            
                            {/* LiveChat Component Embed Plein Écran avec en-tête intégré */}
                            <div className="flex-1 overflow-hidden relative h-full flex flex-col">
                                <LiveChat 
                                    key={`chat_${activeClientId}_m${selectedMedium.id}`}
                                    currentMedium={selectedMedium} 
                                    guestUser={guestUser || ({ name: activeClient.name, phone: activeClient.phone })}
                                    onClose={handleCloseOrMinimizeChat}
                                    embedded={true}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="h-[78vh] rounded-3xl bg-[#12102A]/40 border border-purple-900/30 flex flex-col items-center justify-center p-6 text-center text-purple-300/70 space-y-3">
                            <div className="w-16 h-16 rounded-full bg-purple-950/60 border border-purple-500/30 flex items-center justify-center shadow-inner">
                                <MessageCircleIcon className="w-8 h-8 text-purple-400" />
                            </div>
                            <h2 className="text-base font-bold text-white">
                                Sélectionnez une discussion
                            </h2>
                            <p className="text-xs max-w-sm text-purple-300/70">
                                Cliquez sur une conversation à gauche ou démarrez un nouvel échange avec l'un de nos voyants.
                            </p>
                            <button
                                onClick={() => setActiveTab('directory')}
                                className="px-4 py-2 rounded-xl bg-purple-900/50 hover:bg-purple-800 text-purple-200 text-xs font-bold transition-all"
                            >
                                Voir les voyants disponibles →
                            </button>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default MessagesPage;
